import { useEffect, useRef, useState } from "react";
import { ArrowRight, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";

import { ApiError } from "~/lib/api";
import { isFirstUserError, useLogin, useRegister } from "~/lib/auth";
import { useSubmitMFA } from "~/lib/mfa";

/**
 * Login page rendered when AuthGate sees no current user.
 *
 * Two modes:
 *   - Login (default): email + password → /auth/login.
 *   - Register: only available when the database has zero users (the very
 *     first bootstrap). After the first account, the backend returns 403
 *     and we hide the toggle.
 *
 * The page tries `register` first on submit when the user is in register
 * mode; if 403 (closed), it transparently switches to login mode and shows
 * a hint. This avoids "register" lingering on the screen for a fresh
 * deploy after the first admin has been created elsewhere.
 */
export function LoginPage(): React.ReactElement {
    const [mode, setMode] = useState<"login" | "register">("login");
    const [registerAvailable, setRegisterAvailable] = useState(true);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [challengeToken, setChallengeToken] = useState<string | null>(null);
    const [mfaCode, setMfaCode] = useState("");
    const mfaInputRef = useRef<HTMLInputElement>(null);

    const login = useLogin();
    const register = useRegister();
    const submitMfa = useSubmitMFA();
    const busy = login.isPending || register.isPending || submitMfa.isPending;

    // Probe register availability at mount: try a HEAD-like cheap call.
    // We don't have a /can-register endpoint yet, so just keep the toggle
    // visible until a real attempt fails.
    useEffect(() => {
        if (challengeToken) {
            document.title = "Two-factor · IntelRoom";
            queueMicrotask(() => mfaInputRef.current?.focus());
            return;
        }
        document.title = mode === "register" ? "Create the first admin · IntelRoom" : "Sign in · IntelRoom";
    }, [mode, challengeToken]);

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);

        const trimmed = email.trim();
        if (!trimmed || !password) {
            setError("Email and password are required.");
            return;
        }
        if (mode === "register" && password.length < 12) {
            setError("Password must be at least 12 characters.");
            return;
        }

        try {
            if (mode === "register") {
                await register.mutateAsync({ email: trimmed, password });
            } else {
                const res = await login.mutateAsync({ email: trimmed, password });
                if (res.mfaRequired) {
                    setChallengeToken(res.challengeToken);
                    setMfaCode("");
                }
            }
        } catch (err) {
            if (mode === "register" && isFirstUserError(err)) {
                setRegisterAvailable(false);
                setMode("login");
                setError("This platform already has accounts — sign in instead.");
                return;
            }
            if (err instanceof ApiError) {
                setError(err.message);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("Something went wrong. Try again.");
            }
        }
    }

    async function onMfaSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        const code = mfaCode.replace(/\s+/g, "");
        if (!/^\d{6}$/.test(code)) {
            setError("Enter the 6-digit code from your authenticator.");
            return;
        }
        try {
            await submitMfa.mutateAsync({ challengeToken: challengeToken!, code });
            // useSubmitMFA writes the user into cache; AuthGate re-renders.
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
                if (err.status === 401 && err.message.includes("challenge")) {
                    // challenge expired or already consumed — bounce back to password.
                    setChallengeToken(null);
                }
            } else {
                setError(err instanceof Error ? err.message : "Something went wrong.");
            }
        }
    }

    return (
        <div
            style={{
                display: "grid",
                placeItems: "center",
                minHeight: "100dvh",
                background: "var(--color-bg)",
                padding: "var(--space-6)",
            }}
        >
            <div
                className="surface-raised stack-lg"
                style={{
                    width: "100%",
                    maxWidth: 400,
                    padding: "var(--space-8) var(--space-6)",
                }}
            >
                <div className="stack-sm" style={{ alignItems: "center", textAlign: "center" }}>
                    <div className="brandmark" style={{ padding: 0 }}>
                        <span className="brandmark__dot" aria-hidden />
                        <span className="type-h2" style={{ letterSpacing: "var(--letter-spacing-tight)" }}>
                            IntelRoom
                        </span>
                    </div>
                    <div className="type-caption">
                        {challengeToken
                            ? "Two-factor verification"
                            : mode === "register"
                              ? "Create the first admin"
                              : "Sign in"}
                    </div>
                </div>

                {challengeToken && (
                    <form onSubmit={onMfaSubmit} className="stack">
                        <div className="cluster-xs text-text-muted text-13" style={{ justifyContent: "center" }}>
                            <ShieldCheck size={14} />
                            <span>Enter the code from your authenticator app.</span>
                        </div>
                        <input
                            ref={mfaInputRef}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="\d{6}"
                            maxLength={6}
                            className="input"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                            placeholder="123 456"
                            style={{
                                textAlign: "center",
                                fontFamily: "var(--font-family-mono)",
                                fontSize: "var(--font-size-22)",
                                letterSpacing: "0.4em",
                            }}
                        />

                        {error && (
                            <div
                                className="chip chip--danger shake"
                                style={{ alignSelf: "stretch", height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                role="alert"
                            >
                                {error}
                            </div>
                        )}

                        <button type="submit" className="btn btn--primary press-crush" disabled={busy}>
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                            <span>{busy ? "Verifying…" : "Verify and sign in"}</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                                setChallengeToken(null);
                                setMfaCode("");
                                setError(null);
                            }}
                        >
                            Back to sign in
                        </button>
                    </form>
                )}
                {!challengeToken && (
                <form onSubmit={onSubmit} className="stack">
                    <label className="stack-xs">
                        <span className="text-12 text-text-muted">Email</span>
                        <input
                            type="email"
                            autoComplete="username"
                            autoFocus
                            required
                            className="input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@dataflowx.com"
                        />
                    </label>

                    <label className="stack-xs">
                        <span className="text-12 text-text-muted">Password</span>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showPw ? "text" : "password"}
                                autoComplete={mode === "register" ? "new-password" : "current-password"}
                                required
                                minLength={mode === "register" ? 12 : 1}
                                className="input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={mode === "register" ? "12+ characters" : "Your password"}
                                style={{ paddingRight: 38 }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                aria-label={showPw ? "Hide password" : "Show password"}
                                className="btn btn--ghost btn--sm"
                                style={{
                                    position: "absolute",
                                    right: 4,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    minHeight: 28,
                                }}
                            >
                                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </label>

                    {error && (
                        <div
                            className="chip chip--danger shake"
                            style={{ alignSelf: "stretch", height: "auto", padding: "var(--space-2) var(--space-3)" }}
                            role="alert"
                        >
                            {error}
                        </div>
                    )}

                    <button type="submit" className="btn btn--primary press-crush" disabled={busy}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                        <span>{mode === "register" ? "Create account" : "Sign in"}</span>
                    </button>
                </form>
                )}

                {!challengeToken && registerAvailable && (
                    <div className="text-12 text-text-muted" style={{ textAlign: "center" }}>
                        {mode === "login" ? (
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                    setError(null);
                                    setMode("register");
                                }}
                            >
                                First time? Create the bootstrap admin
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                    setError(null);
                                    setMode("login");
                                }}
                            >
                                Already have an account? Sign in
                            </button>
                        )}
                    </div>
                )}

                <div className="text-12 text-text-subtle" style={{ textAlign: "center" }}>
                    DataFlowX · IntelRoom V2
                </div>
            </div>
        </div>
    );
}
